import { createContext, useContext, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type UserProfile } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { useAuth } from "@/contexts/AuthContext";

export interface UserProfileContextValue {
  profile: UserProfile | null;
  credits: number | null;
  tier: string | null;
  profileLoading: boolean;
  refreshProfile: () => Promise<void>;
  updateProfile: (patch: { displayName?: string | null; organisation?: string | null }) => Promise<UserProfile>;
}

const UserProfileContext = createContext<UserProfileContextValue | undefined>(undefined);

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, authLoading } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile = null, isLoading: profileLoading } = useQuery({
    queryKey: qk.profile,
    queryFn: () => api.user.getProfile(),
    enabled: !authLoading && isAuthenticated
  });

  const refreshProfile = async () => {
    if (!isAuthenticated) {
      queryClient.setQueryData(qk.profile, null);
      return;
    }

    await queryClient.invalidateQueries({ queryKey: qk.profile });
  };

  const updateMutation = useMutation({
    mutationFn: (patch: { displayName?: string | null; organisation?: string | null }) => api.user.updateProfile(patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: qk.profile });
      const previous = queryClient.getQueryData<UserProfile>(qk.profile);
      if (previous) {
        queryClient.setQueryData<UserProfile>(qk.profile, { ...previous, ...patch });
      }
      return { previous };
    },
    onError: (_error, _patch, context) => {
      queryClient.setQueryData(qk.profile, context?.previous ?? null);
    },
    onSuccess: (nextProfile) => {
      queryClient.setQueryData(qk.profile, nextProfile);
    }
  });

  const updateProfile = (patch: { displayName?: string | null; organisation?: string | null }) => updateMutation.mutateAsync(patch);

  const value: UserProfileContextValue = {
    profile,
    credits: profile?.creditsRemaining ?? null,
    tier: profile?.tier ?? null,
    profileLoading,
    refreshProfile,
    updateProfile
  };

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>;
}

export function useUserProfile() {
  const value = useContext(UserProfileContext);
  if (!value) {
    throw new Error("useUserProfile must be used within UserProfileProvider");
  }

  return value;
}
